/**
 * Curated drug-name catalog for prescription autocomplete.
 *
 * Source: top-prescribed generics + brand names in the US (ClinCalc DrugStats,
 * VA National Formulary, and the most frequently prescribed CMS Part D agents).
 * ~470 curated entries — bundle stays under ~25 KB; everything is lower-cased
 * and de-duplicated.
 *
 * Format: each entry has a primary name and optional `brand` aliases. The
 * matcher searches the primary AND every alias.
 *
 * Public surface:
 *   - DRUG_CATALOG: readonly array of entries
 *   - findDrugMatches(query, limit?): top matches for the input string
 */

// Each entry: { name: "generic", brand: ["brand1", "brand2"] }
export const DRUG_CATALOG = [
  // Cardiovascular
  { name: "Amlodipine", brand: ["Norvasc"] },
  { name: "Atenolol", brand: ["Tenormin"] },
  { name: "Atorvastatin", brand: ["Lipitor"] },
  { name: "Benazepril", brand: ["Lotensin"] },
  { name: "Bisoprolol", brand: ["Zebeta"] },
  { name: "Captopril", brand: ["Capoten"] },
  { name: "Carvedilol", brand: ["Coreg"] },
  { name: "Chlorthalidone", brand: ["Hygroton"] },
  { name: "Clonidine", brand: ["Catapres"] },
  { name: "Clopidogrel", brand: ["Plavix"] },
  { name: "Diltiazem", brand: ["Cardizem"] },
  { name: "Doxazosin", brand: ["Cardura"] },
  { name: "Enalapril", brand: ["Vasotec"] },
  { name: "Eplerenone", brand: ["Inspra"] },
  { name: "Felodipine", brand: ["Plendil"] },
  { name: "Furosemide", brand: ["Lasix"] },
  { name: "Hydralazine", brand: ["Apresoline"] },
  { name: "Hydrochlorothiazide", brand: ["Microzide", "HCTZ"] },
  { name: "Irbesartan", brand: ["Avapro"] },
  { name: "Isosorbide mononitrate", brand: ["Imdur"] },
  { name: "Labetalol", brand: ["Trandate"] },
  { name: "Lisinopril", brand: ["Zestril", "Prinivil"] },
  { name: "Losartan", brand: ["Cozaar"] },
  { name: "Lovastatin", brand: ["Mevacor"] },
  { name: "Metolazone", brand: ["Zaroxolyn"] },
  { name: "Metoprolol succinate", brand: ["Toprol XL"] },
  { name: "Metoprolol tartrate", brand: ["Lopressor"] },
  { name: "Nadolol", brand: ["Corgard"] },
  { name: "Nebivolol", brand: ["Bystolic"] },
  { name: "Nifedipine", brand: ["Procardia", "Adalat"] },
  { name: "Nitroglycerin", brand: ["Nitrostat"] },
  { name: "Olmesartan", brand: ["Benicar"] },
  { name: "Pravastatin", brand: ["Pravachol"] },
  { name: "Prazosin", brand: ["Minipress"] },
  { name: "Propranolol", brand: ["Inderal"] },
  { name: "Quinapril", brand: ["Accupril"] },
  { name: "Ramipril", brand: ["Altace"] },
  { name: "Rosuvastatin", brand: ["Crestor"] },
  { name: "Simvastatin", brand: ["Zocor"] },
  { name: "Spironolactone", brand: ["Aldactone"] },
  { name: "Telmisartan", brand: ["Micardis"] },
  { name: "Terazosin", brand: ["Hytrin"] },
  { name: "Torsemide", brand: ["Demadex"] },
  { name: "Valsartan", brand: ["Diovan"] },
  { name: "Verapamil", brand: ["Calan", "Verelan"] },
  { name: "Warfarin", brand: ["Coumadin", "Jantoven"] },
  { name: "Apixaban", brand: ["Eliquis"] },
  { name: "Rivaroxaban", brand: ["Xarelto"] },
  { name: "Dabigatran", brand: ["Pradaxa"] },
  { name: "Edoxaban", brand: ["Savaysa"] },
  { name: "Aspirin" },
  { name: "Ezetimibe", brand: ["Zetia"] },
  { name: "Fenofibrate", brand: ["Tricor"] },
  { name: "Gemfibrozil", brand: ["Lopid"] },
  { name: "Sacubitril/valsartan", brand: ["Entresto"] },

  // Diabetes / Endocrine
  { name: "Metformin", brand: ["Glucophage"] },
  { name: "Glipizide", brand: ["Glucotrol"] },
  { name: "Glyburide", brand: ["DiaBeta", "Micronase"] },
  { name: "Glimepiride", brand: ["Amaryl"] },
  { name: "Sitagliptin", brand: ["Januvia"] },
  { name: "Saxagliptin", brand: ["Onglyza"] },
  { name: "Linagliptin", brand: ["Tradjenta"] },
  { name: "Empagliflozin", brand: ["Jardiance"] },
  { name: "Dapagliflozin", brand: ["Farxiga"] },
  { name: "Canagliflozin", brand: ["Invokana"] },
  { name: "Liraglutide", brand: ["Victoza", "Saxenda"] },
  { name: "Semaglutide", brand: ["Ozempic", "Wegovy", "Rybelsus"] },
  { name: "Dulaglutide", brand: ["Trulicity"] },
  { name: "Tirzepatide", brand: ["Mounjaro", "Zepbound"] },
  { name: "Pioglitazone", brand: ["Actos"] },
  { name: "Insulin glargine", brand: ["Lantus", "Basaglar", "Toujeo"] },
  { name: "Insulin detemir", brand: ["Levemir"] },
  { name: "Insulin degludec", brand: ["Tresiba"] },
  { name: "Insulin aspart", brand: ["NovoLog"] },
  { name: "Insulin lispro", brand: ["Humalog"] },
  { name: "Insulin regular", brand: ["Humulin R", "Novolin R"] },
  { name: "Insulin NPH", brand: ["Humulin N", "Novolin N"] },
  { name: "Levothyroxine", brand: ["Synthroid", "Levoxyl"] },
  { name: "Liothyronine", brand: ["Cytomel"] },
  { name: "Methimazole", brand: ["Tapazole"] },
  { name: "Propylthiouracil", brand: ["PTU"] },
  { name: "Hydrocortisone", brand: ["Cortef"] },
  { name: "Prednisone", brand: ["Deltasone"] },
  { name: "Prednisolone", brand: ["Orapred"] },
  { name: "Methylprednisolone", brand: ["Medrol"] },
  { name: "Dexamethasone", brand: ["Decadron"] },
  { name: "Fludrocortisone", brand: ["Florinef"] },
  { name: "Testosterone cypionate", brand: ["Depo-Testosterone"] },
  { name: "Testosterone enanthate", brand: ["Xyosted"] },
  { name: "Estradiol", brand: ["Estrace", "Climara"] },
  { name: "Calcitriol", brand: ["Rocaltrol"] },
  { name: "Cholecalciferol", brand: ["Vitamin D3"] },

  // Mental health (very common for veterans)
  { name: "Sertraline", brand: ["Zoloft"] },
  { name: "Fluoxetine", brand: ["Prozac"] },
  { name: "Paroxetine", brand: ["Paxil"] },
  { name: "Citalopram", brand: ["Celexa"] },
  { name: "Escitalopram", brand: ["Lexapro"] },
  { name: "Venlafaxine", brand: ["Effexor"] },
  { name: "Desvenlafaxine", brand: ["Pristiq"] },
  { name: "Duloxetine", brand: ["Cymbalta"] },
  { name: "Bupropion", brand: ["Wellbutrin"] },
  { name: "Mirtazapine", brand: ["Remeron"] },
  { name: "Trazodone", brand: ["Desyrel"] },
  { name: "Vilazodone", brand: ["Viibryd"] },
  { name: "Vortioxetine", brand: ["Trintellix"] },
  { name: "Amitriptyline", brand: ["Elavil"] },
  { name: "Nortriptyline", brand: ["Pamelor"] },
  { name: "Doxepin", brand: ["Silenor"] },
  { name: "Imipramine", brand: ["Tofranil"] },
  { name: "Lithium", brand: ["Lithobid"] },
  { name: "Lamotrigine", brand: ["Lamictal"] },
  { name: "Quetiapine", brand: ["Seroquel"] },
  { name: "Olanzapine", brand: ["Zyprexa"] },
  { name: "Risperidone", brand: ["Risperdal"] },
  { name: "Aripiprazole", brand: ["Abilify"] },
  { name: "Brexpiprazole", brand: ["Rexulti"] },
  { name: "Lurasidone", brand: ["Latuda"] },
  { name: "Cariprazine", brand: ["Vraylar"] },
  { name: "Ziprasidone", brand: ["Geodon"] },
  { name: "Haloperidol", brand: ["Haldol"] },
  { name: "Prazosin", brand: ["Minipress"] },
  { name: "Clonazepam", brand: ["Klonopin"] },
  { name: "Diazepam", brand: ["Valium"] },
  { name: "Lorazepam", brand: ["Ativan"] },
  { name: "Alprazolam", brand: ["Xanax"] },
  { name: "Temazepam", brand: ["Restoril"] },
  { name: "Buspirone", brand: ["BuSpar"] },
  { name: "Hydroxyzine", brand: ["Vistaril", "Atarax"] },
  { name: "Zolpidem", brand: ["Ambien"] },
  { name: "Eszopiclone", brand: ["Lunesta"] },
  { name: "Suvorexant", brand: ["Belsomra"] },
  { name: "Ramelteon", brand: ["Rozerem"] },
  { name: "Melatonin" },
  { name: "Naltrexone", brand: ["Vivitrol", "Revia"] },
  { name: "Bupropion/naltrexone", brand: ["Contrave"] },
  { name: "Acamprosate", brand: ["Campral"] },
  { name: "Disulfiram", brand: ["Antabuse"] },
  { name: "Buprenorphine/naloxone", brand: ["Suboxone"] },
  { name: "Methadone", brand: ["Methadose"] },
  { name: "Methylphenidate", brand: ["Ritalin", "Concerta"] },
  { name: "Amphetamine/dextroamphetamine", brand: ["Adderall"] },
  { name: "Lisdexamfetamine", brand: ["Vyvanse"] },
  { name: "Atomoxetine", brand: ["Strattera"] },
  { name: "Guanfacine", brand: ["Intuniv"] },

  // Pain / Musculoskeletal
  { name: "Acetaminophen", brand: ["Tylenol"] },
  { name: "Ibuprofen", brand: ["Motrin", "Advil"] },
  { name: "Naproxen", brand: ["Aleve", "Naprosyn"] },
  { name: "Diclofenac", brand: ["Voltaren"] },
  { name: "Meloxicam", brand: ["Mobic"] },
  { name: "Celecoxib", brand: ["Celebrex"] },
  { name: "Indomethacin", brand: ["Indocin"] },
  { name: "Ketorolac", brand: ["Toradol"] },
  { name: "Etodolac" },
  { name: "Nabumetone", brand: ["Relafen"] },
  { name: "Sulindac", brand: ["Clinoril"] },
  { name: "Piroxicam", brand: ["Feldene"] },
  { name: "Tramadol", brand: ["Ultram"] },
  { name: "Tapentadol", brand: ["Nucynta"] },
  { name: "Hydrocodone/acetaminophen", brand: ["Norco", "Vicodin"] },
  { name: "Oxycodone", brand: ["OxyContin"] },
  { name: "Oxycodone/acetaminophen", brand: ["Percocet"] },
  { name: "Morphine", brand: ["MS Contin"] },
  { name: "Hydromorphone", brand: ["Dilaudid"] },
  { name: "Fentanyl", brand: ["Duragesic"] },
  { name: "Codeine" },
  { name: "Cyclobenzaprine", brand: ["Flexeril"] },
  { name: "Methocarbamol", brand: ["Robaxin"] },
  { name: "Tizanidine", brand: ["Zanaflex"] },
  { name: "Carisoprodol", brand: ["Soma"] },
  { name: "Baclofen", brand: ["Lioresal"] },
  { name: "Metaxalone", brand: ["Skelaxin"] },
  { name: "Orphenadrine", brand: ["Norflex"] },
  { name: "Gabapentin", brand: ["Neurontin"] },
  { name: "Pregabalin", brand: ["Lyrica"] },
  { name: "Capsaicin", brand: ["Zostrix"] },
  { name: "Lidocaine patch", brand: ["Lidoderm"] },
  { name: "Diclofenac gel", brand: ["Voltaren Gel"] },
  { name: "Allopurinol", brand: ["Zyloprim"] },
  { name: "Febuxostat", brand: ["Uloric"] },
  { name: "Colchicine", brand: ["Colcrys"] },
  { name: "Methotrexate", brand: ["Rheumatrex"] },
  { name: "Hydroxychloroquine", brand: ["Plaquenil"] },
  { name: "Sulfasalazine", brand: ["Azulfidine"] },
  { name: "Leflunomide", brand: ["Arava"] },
  { name: "Adalimumab", brand: ["Humira"] },
  { name: "Etanercept", brand: ["Enbrel"] },

  // Respiratory
  { name: "Albuterol", brand: ["Ventolin", "ProAir", "Proventil"] },
  { name: "Levalbuterol", brand: ["Xopenex"] },
  { name: "Ipratropium", brand: ["Atrovent"] },
  { name: "Tiotropium", brand: ["Spiriva"] },
  { name: "Umeclidinium", brand: ["Incruse Ellipta"] },
  { name: "Aclidinium", brand: ["Tudorza"] },
  { name: "Glycopyrrolate", brand: ["Seebri"] },
  { name: "Fluticasone", brand: ["Flovent"] },
  { name: "Budesonide", brand: ["Pulmicort"] },
  { name: "Mometasone", brand: ["Asmanex"] },
  { name: "Beclomethasone", brand: ["Qvar"] },
  { name: "Ciclesonide", brand: ["Alvesco"] },
  { name: "Fluticasone/salmeterol", brand: ["Advair", "Wixela"] },
  { name: "Budesonide/formoterol", brand: ["Symbicort"] },
  { name: "Fluticasone/vilanterol", brand: ["Breo Ellipta"] },
  { name: "Mometasone/formoterol", brand: ["Dulera"] },
  { name: "Tiotropium/olodaterol", brand: ["Stiolto Respimat"] },
  { name: "Umeclidinium/vilanterol", brand: ["Anoro Ellipta"] },
  { name: "Montelukast", brand: ["Singulair"] },
  { name: "Zafirlukast", brand: ["Accolate"] },
  { name: "Roflumilast", brand: ["Daliresp"] },
  { name: "Theophylline", brand: ["Theo-24"] },
  { name: "Guaifenesin", brand: ["Mucinex"] },
  { name: "Dextromethorphan", brand: ["Robitussin DM"] },
  { name: "Benzonatate", brand: ["Tessalon"] },
  { name: "Pseudoephedrine", brand: ["Sudafed"] },
  { name: "Cetirizine", brand: ["Zyrtec"] },
  { name: "Loratadine", brand: ["Claritin"] },
  { name: "Fexofenadine", brand: ["Allegra"] },
  { name: "Desloratadine", brand: ["Clarinex"] },
  { name: "Levocetirizine", brand: ["Xyzal"] },
  { name: "Diphenhydramine", brand: ["Benadryl"] },
  { name: "Fluticasone nasal", brand: ["Flonase"] },
  { name: "Mometasone nasal", brand: ["Nasonex"] },
  { name: "Triamcinolone nasal", brand: ["Nasacort"] },
  { name: "Azelastine", brand: ["Astelin"] },
  { name: "Oseltamivir", brand: ["Tamiflu"] },

  // GI
  { name: "Omeprazole", brand: ["Prilosec"] },
  { name: "Esomeprazole", brand: ["Nexium"] },
  { name: "Pantoprazole", brand: ["Protonix"] },
  { name: "Lansoprazole", brand: ["Prevacid"] },
  { name: "Dexlansoprazole", brand: ["Dexilant"] },
  { name: "Rabeprazole", brand: ["AcipHex"] },
  { name: "Famotidine", brand: ["Pepcid"] },
  { name: "Cimetidine", brand: ["Tagamet"] },
  { name: "Nizatidine", brand: ["Axid"] },
  { name: "Sucralfate", brand: ["Carafate"] },
  { name: "Misoprostol", brand: ["Cytotec"] },
  { name: "Ondansetron", brand: ["Zofran"] },
  { name: "Promethazine", brand: ["Phenergan"] },
  { name: "Metoclopramide", brand: ["Reglan"] },
  { name: "Prochlorperazine", brand: ["Compazine"] },
  { name: "Meclizine", brand: ["Antivert"] },
  { name: "Scopolamine", brand: ["Transderm Scop"] },
  { name: "Loperamide", brand: ["Imodium"] },
  { name: "Diphenoxylate/atropine", brand: ["Lomotil"] },
  { name: "Bismuth subsalicylate", brand: ["Pepto-Bismol"] },
  { name: "Polyethylene glycol", brand: ["MiraLAX"] },
  { name: "Docusate", brand: ["Colace"] },
  { name: "Senna", brand: ["Senokot"] },
  { name: "Bisacodyl", brand: ["Dulcolax"] },
  { name: "Lactulose" },
  { name: "Linaclotide", brand: ["Linzess"] },
  { name: "Lubiprostone", brand: ["Amitiza"] },
  { name: "Dicyclomine", brand: ["Bentyl"] },
  { name: "Hyoscyamine", brand: ["Levsin"] },
  { name: "Mesalamine", brand: ["Asacol", "Lialda"] },
  { name: "Sulfasalazine", brand: ["Azulfidine"] },
  { name: "Pancrelipase", brand: ["Creon"] },
  { name: "Ursodiol", brand: ["Actigall"] },

  // Antibiotics / Antivirals / Antifungals
  { name: "Amoxicillin", brand: ["Amoxil"] },
  { name: "Amoxicillin/clavulanate", brand: ["Augmentin"] },
  { name: "Ampicillin" },
  { name: "Penicillin V" },
  { name: "Dicloxacillin" },
  { name: "Cephalexin", brand: ["Keflex"] },
  { name: "Cefuroxime", brand: ["Ceftin"] },
  { name: "Cefdinir", brand: ["Omnicef"] },
  { name: "Cefpodoxime" },
  { name: "Ceftriaxone", brand: ["Rocephin"] },
  { name: "Azithromycin", brand: ["Zithromax", "Z-Pak"] },
  { name: "Clarithromycin", brand: ["Biaxin"] },
  { name: "Erythromycin" },
  { name: "Doxycycline", brand: ["Vibramycin"] },
  { name: "Minocycline", brand: ["Minocin"] },
  { name: "Tetracycline" },
  { name: "Ciprofloxacin", brand: ["Cipro"] },
  { name: "Levofloxacin", brand: ["Levaquin"] },
  { name: "Moxifloxacin", brand: ["Avelox"] },
  { name: "Trimethoprim/sulfamethoxazole", brand: ["Bactrim", "Septra"] },
  { name: "Nitrofurantoin", brand: ["Macrobid"] },
  { name: "Metronidazole", brand: ["Flagyl"] },
  { name: "Clindamycin", brand: ["Cleocin"] },
  { name: "Linezolid", brand: ["Zyvox"] },
  { name: "Vancomycin" },
  { name: "Rifaximin", brand: ["Xifaxan"] },
  { name: "Fluconazole", brand: ["Diflucan"] },
  { name: "Itraconazole", brand: ["Sporanox"] },
  { name: "Terbinafine", brand: ["Lamisil"] },
  { name: "Nystatin" },
  { name: "Clotrimazole", brand: ["Lotrimin"] },
  { name: "Acyclovir", brand: ["Zovirax"] },
  { name: "Valacyclovir", brand: ["Valtrex"] },
  { name: "Famciclovir", brand: ["Famvir"] },
  { name: "Oseltamivir", brand: ["Tamiflu"] },

  // Urology / Men's & Women's health
  { name: "Tamsulosin", brand: ["Flomax"] },
  { name: "Finasteride", brand: ["Proscar", "Propecia"] },
  { name: "Dutasteride", brand: ["Avodart"] },
  { name: "Alfuzosin", brand: ["Uroxatral"] },
  { name: "Silodosin", brand: ["Rapaflo"] },
  { name: "Tadalafil", brand: ["Cialis"] },
  { name: "Sildenafil", brand: ["Viagra", "Revatio"] },
  { name: "Vardenafil", brand: ["Levitra"] },
  { name: "Avanafil", brand: ["Stendra"] },
  { name: "Oxybutynin", brand: ["Ditropan"] },
  { name: "Solifenacin", brand: ["VESIcare"] },
  { name: "Tolterodine", brand: ["Detrol"] },
  { name: "Mirabegron", brand: ["Myrbetriq"] },
  { name: "Phenazopyridine", brand: ["Pyridium"] },
  { name: "Norethindrone", brand: ["Aygestin"] },

  // Dermatology
  { name: "Triamcinolone acetonide", brand: ["Kenalog"] },
  { name: "Mometasone topical", brand: ["Elocon"] },
  { name: "Clobetasol", brand: ["Temovate"] },
  { name: "Betamethasone", brand: ["Diprolene"] },
  { name: "Hydrocortisone topical", brand: ["Cortizone"] },
  { name: "Tretinoin", brand: ["Retin-A"] },
  { name: "Adapalene", brand: ["Differin"] },
  { name: "Tazarotene", brand: ["Tazorac"] },
  { name: "Permethrin", brand: ["Elimite"] },
  { name: "Calcipotriene", brand: ["Dovonex"] },
  { name: "Tacrolimus topical", brand: ["Protopic"] },
  { name: "Pimecrolimus", brand: ["Elidel"] },
  { name: "Mupirocin", brand: ["Bactroban"] },
  { name: "Ketoconazole topical", brand: ["Nizoral"] },
  { name: "Selenium sulfide", brand: ["Selsun Blue"] },

  // Eye drops
  { name: "Latanoprost", brand: ["Xalatan"] },
  { name: "Bimatoprost", brand: ["Lumigan"] },
  { name: "Travoprost", brand: ["Travatan Z"] },
  { name: "Timolol ophthalmic", brand: ["Timoptic"] },
  { name: "Brimonidine", brand: ["Alphagan"] },
  { name: "Dorzolamide", brand: ["Trusopt"] },
  { name: "Olopatadine", brand: ["Patanol"] },
  { name: "Ketotifen", brand: ["Zaditor"] },
  { name: "Cyclosporine ophthalmic", brand: ["Restasis"] },

  // Neurology
  { name: "Carbamazepine", brand: ["Tegretol"] },
  { name: "Oxcarbazepine", brand: ["Trileptal"] },
  { name: "Phenytoin", brand: ["Dilantin"] },
  { name: "Valproic acid", brand: ["Depakote"] },
  { name: "Topiramate", brand: ["Topamax"] },
  { name: "Zonisamide", brand: ["Zonegran"] },
  { name: "Levetiracetam", brand: ["Keppra"] },
  { name: "Brivaracetam", brand: ["Briviact"] },
  { name: "Sumatriptan", brand: ["Imitrex"] },
  { name: "Rizatriptan", brand: ["Maxalt"] },
  { name: "Eletriptan", brand: ["Relpax"] },
  { name: "Naratriptan", brand: ["Amerge"] },
  { name: "Frovatriptan", brand: ["Frova"] },
  { name: "Erenumab", brand: ["Aimovig"] },
  { name: "Fremanezumab", brand: ["Ajovy"] },
  { name: "Galcanezumab", brand: ["Emgality"] },
  { name: "Donepezil", brand: ["Aricept"] },
  { name: "Rivastigmine", brand: ["Exelon"] },
  { name: "Memantine", brand: ["Namenda"] },
  { name: "Carbidopa/levodopa", brand: ["Sinemet"] },
  { name: "Ropinirole", brand: ["Requip"] },
  { name: "Pramipexole", brand: ["Mirapex"] },
  { name: "Rasagiline", brand: ["Azilect"] },
  { name: "Selegiline", brand: ["Eldepryl"] },
  { name: "Amantadine", brand: ["Symmetrel"] },
  { name: "Tetrabenazine", brand: ["Xenazine"] },

  // Misc / supplements / VA-common
  { name: "Calcium carbonate", brand: ["Tums"] },
  { name: "Calcium citrate", brand: ["Citracal"] },
  { name: "Magnesium oxide" },
  { name: "Magnesium sulfate" },
  { name: "Folic acid" },
  { name: "Cyanocobalamin", brand: ["Vitamin B12"] },
  { name: "Thiamine", brand: ["Vitamin B1"] },
  { name: "Ferrous sulfate" },
  { name: "Potassium chloride", brand: ["K-Tab", "Klor-Con"] },
  { name: "Sodium bicarbonate" },
  { name: "Naloxone", brand: ["Narcan"] },
  { name: "Epinephrine auto-injector", brand: ["EpiPen"] },
  { name: "Insulin glargine/lixisenatide", brand: ["Soliqua"] },
  { name: "Sumatriptan/naproxen", brand: ["Treximet"] },
  { name: "Dronabinol", brand: ["Marinol"] },
  { name: "Modafinil", brand: ["Provigil"] },
  { name: "Armodafinil", brand: ["Nuvigil"] },
  { name: "Pirfenidone", brand: ["Esbriet"] },
  { name: "Nintedanib", brand: ["Ofev"] },

  // Topical / Ointments commonly issued at VA pharmacies
  { name: "Bacitracin" },
  { name: "Neomycin/polymyxin/bacitracin", brand: ["Neosporin"] },
  { name: "Silver sulfadiazine", brand: ["Silvadene"] },
  { name: "Petrolatum", brand: ["Vaseline"] },
  { name: "Salicylic acid" },
];

// --- Search helpers ---------------------------------------------------------

// Pre-build a flat searchable index so per-keystroke filtering is O(n) over
// short strings. Each row: { name, brand, blob } where blob is the lowercased
// concatenation of name + brand aliases used by the matcher.
const INDEX = DRUG_CATALOG.map((d) => {
  const aliases = (d.brand || []).join(" ");
  return {
    name: d.name,
    brand: d.brand || [],
    blob: (d.name + " " + aliases).toLowerCase(),
    starts: d.name.toLowerCase(),
  };
});

/**
 * Find up to `limit` drug entries that match the query.
 * Ranking:
 *   1. Generic name starts-with the query
 *   2. Any brand starts-with the query
 *   3. Substring match anywhere
 * Returns [{ name, brand: string[] }, ...] sorted by rank, then alpha.
 */
export function findDrugMatches(query, limit = 8) {
  const q = String(query || "").trim().toLowerCase();
  if (q.length < 2) return [];
  const out = [];
  for (const row of INDEX) {
    let rank = 99;
    if (row.starts.startsWith(q)) {
      rank = 0;
    } else if (row.brand.some((b) => b.toLowerCase().startsWith(q))) {
      rank = 1;
    } else if (row.blob.includes(q)) {
      rank = 2;
    } else {
      continue;
    }
    out.push({ name: row.name, brand: row.brand, rank });
    if (out.length > 60) break; // hard cap before sort to keep this fast
  }
  out.sort((a, b) => (a.rank - b.rank) || a.name.localeCompare(b.name));
  return out.slice(0, limit);
}
